import React from 'react';
import { motion } from 'framer-motion';
import { Typography, Card, Timeline } from 'antd';
import { EnvironmentOutlined, TeamOutlined, BulbOutlined } from '@ant-design/icons';

const { Title, Paragraph } = Typography;

const About = () => {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerChildren = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const slideIn = {
    initial: { x: -20, opacity: 0 },
    animate: { x: 0, opacity: 1 },
    transition: { duration: 0.5 }
  };

  return (
    <motion.div 
      className="max-h-screen bg-indigo-50 py-12 px-4 sm:px-6 lg:px-8 overflow-y-scroll"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <motion.div
        className="max-w-4xl mx-auto"
        variants={staggerChildren}
        initial="initial"
        animate="animate"
      >
        <motion.h1 
          className="text-center text-4xl font-bold text-indigo-800 mb-8"
          variants={fadeIn}
        >
          À Propos d'ERS
        </motion.h1>

        <motion.div variants={fadeIn} className="mb-12">
          <Card className="shadow-lg">
            <Paragraph className="text-lg text-gray-700">
              Solutions de Ressources Environnementales (ERS) est une entreprise leader dans la gestion de l'irrigation et des exploitations agricoles. 
              Avec des années d'expérience et un engagement envers des pratiques durables, nous aidons les agriculteurs à optimiser 
              leurs ressources et à augmenter leur productivité tout en minimisant l'impact environnemental.
            </Paragraph>
          </Card>
        </motion.div>

        <motion.div variants={fadeIn} className="mb-12">
          <motion.h2 
            className="text-2xl font-semibold text-indigo-700 mb-4"
            variants={slideIn}
          >
            Notre Mission
          </motion.h2>
          <motion.div
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <Card className="shadow-lg bg-indigo-600">
              <Paragraph className="text-white text-lg">
                Révolutionner les pratiques agricoles grâce à des solutions d'irrigation innovantes et 
                des techniques de gestion agricole durables, assurant la sécurité alimentaire et la préservation de l'environnement 
                pour les générations futures.
              </Paragraph>
            </Card>
          </motion.div>
        </motion.div>

        <motion.div variants={fadeIn} className="mb-12">
          <motion.h2 
            className="text-2xl font-semibold text-indigo-700 mb-4"
            variants={slideIn}
          >
            Nos Valeurs
          </motion.h2>
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
            variants={staggerChildren}
          >
            {[
              { icon: EnvironmentOutlined, title: "Durabilité", desc: "Promouvoir des pratiques écologiques dans toutes nos opérations." },
              { icon: TeamOutlined, title: "Collaboration", desc: "Travailler en étroite collaboration avec les agriculteurs pour atteindre leurs objectifs." },
              { icon: BulbOutlined, title: "Innovation", desc: "Améliorer continuellement nos méthodes et technologies." }
            ].map((item, index) => (
              <motion.div key={index} variants={fadeIn}>
                <motion.div
                  whileHover={{ scale: 1.05, rotate: 2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Card className="shadow-md hover:shadow-xl transition-shadow duration-300">
                    <item.icon className="text-4xl text-indigo-600 mb-2" />
                    <Title level={4} className="text-indigo-800">{item.title}</Title>
                    <Paragraph>{item.desc}</Paragraph>
                  </Card>
                </motion.div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        <motion.div variants={fadeIn}>
          <motion.h2 
            className="text-2xl font-semibold text-indigo-700 mb-4"
            variants={slideIn}
          >
            Notre Parcours
          </motion.h2>
          <motion.div
            className="bg-white p-6 rounded-lg shadow-lg"
            whileHover={{ scale: 1.02 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <Timeline mode="alternate">
              {[
                "2010 - Fondation d'ERS",
                "2013 - Lancement du premier système d'irrigation intelligent",
                "2016 - Extension des services à la gestion complète des exploitations",
                "2019 - Introduction de la prédiction des cultures par IA",
                "2022 - Plus de 1000 exploitations agricoles servies",
                "2024 - Expansion et innovation continues"
              ].map((item, index) => (
                <Timeline.Item 
                  key={index} 
                  color="indigo"
                >
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.2 }}
                  >
                    {item}
                  </motion.div>
                </Timeline.Item>
              ))}
            </Timeline>
          </motion.div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default About;